import { Button, MessageBox, Table, TableCell, TableHeadCell, TableRow } from "@rothoblaas/design-system";
import { memo, useCallback, useMemo, type FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import {
  BOARD_FIT_TOLERANCE_MM,
  MAX_BOARD_FIT_COUNT,
  type BoardFit,
  type BoardQuantity,
} from "../../../http/deckDesigner/formulas";
import { RECTANGULAR_SHAPE, type DifferentBoardPosition } from "../../../http/deckDesigner/types/configurations";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import styles from "./BoardFitWarning.module.css";
import { hasMatchingDimension, isNonNegativeNumber, isPositiveNumber } from "./deckGeometry";
import { useMeasurementFormat } from "./measurementFormat";

type BoardFitSnapshot = {
  base: number | null;
  height: number | null;
  secondaryBase: number | null;
  secondaryHeight: number | null;
  boardWidth: number | null;
  longDifferentBoardWidth: number | null;
  shortDifferentBoardWidth: number | null;
  longDifferentBoardPosition: DifferentBoardPosition | null;
  shortDifferentBoardPosition: DifferentBoardPosition | null;
};

// One row per value the applied fix changed, shown as previous vs. current in the "applied" box.
type AppliedFitRow = {
  label: string;
  previous: string;
  current: string;
};

export type AppliedFitInfo = {
  snapshot: BoardFitSnapshot;
  rows: AppliedFitRow[];
};

type BoardFitWarningProps = {
  boardFit: BoardFit | null;
  boardQuantity: BoardQuantity | null;
  onApplyBoardFit: () => void;
  onApplyDifferentBoardWidthFit: () => void;
  // Owned by DeckDesignerResults, not here: the warning is shown in two results tabs, and applying a fix
  // switches to the preview one, which would otherwise lose the "applied" box and its undo.
  appliedFit: AppliedFitInfo | null;
  onAppliedFitChange: (appliedFit: AppliedFitInfo | null) => void;
  // Called after any of the three fixes is applied, e.g. to show the result in the top view.
  onFitApplied?: () => void;
};

type DimensionFieldName = "base" | "height" | "secondaryBase" | "secondaryHeight";
type FittingDeckDimensions = {
  primaryFieldName: "base" | "height";
  primaryValue: number;
  secondaryFieldName?: "secondaryBase" | "secondaryHeight";
  secondaryValue?: number;
};
type BoardCountMode = "floor" | "round";
type DifferentBoardWidthItem = {
  label: string;
  value: number;
};

const CENTIMETERS_PER_METER = 100;
const MILLIMETERS_PER_CENTIMETER = 10;
const STRICT_INTERVAL_EPSILON_MM = 0.000001;

const getBoardSpanUsedLength = (boardCount: number, boardWidth: number, gap: number): number =>
  boardCount * boardWidth + Math.max(boardCount - 1, 0) * gap;

const getBoardCountLengthBounds = (
  boardCount: number,
  boardWidth: number,
  gap: number,
  countMode: BoardCountMode,
): { minLengthMm: number; maxLengthMm: number } => {
  const boardTotalSpace = boardWidth + gap;

  if (countMode === "floor") {
    return {
      minLengthMm: boardCount * boardTotalSpace - gap,
      maxLengthMm: (boardCount + 1) * boardTotalSpace - gap,
    };
  }

  return {
    minLengthMm: (boardCount - 0.5) * boardTotalSpace - gap,
    maxLengthMm: (boardCount + 0.5) * boardTotalSpace - gap,
  };
};

const getFittingCentimetersForBoardCount = (
  boardCount: number,
  boardWidth: number,
  gap: number,
  targetCentimeters: number,
  countMode: BoardCountMode,
): number | null => {
  const usedLengthMm = getBoardSpanUsedLength(boardCount, boardWidth, gap);
  const countBounds = getBoardCountLengthBounds(boardCount, boardWidth, gap, countMode);
  const minCentimeters = Math.max(
    1,
    Math.ceil((usedLengthMm - BOARD_FIT_TOLERANCE_MM) / MILLIMETERS_PER_CENTIMETER),
    Math.ceil((countBounds.minLengthMm - STRICT_INTERVAL_EPSILON_MM) / MILLIMETERS_PER_CENTIMETER),
  );
  const maxCentimeters = Math.min(
    Math.floor(
      (usedLengthMm + Math.max(BOARD_FIT_TOLERANCE_MM, boardCount - STRICT_INTERVAL_EPSILON_MM)) /
        MILLIMETERS_PER_CENTIMETER,
    ),
    Math.floor((countBounds.maxLengthMm - STRICT_INTERVAL_EPSILON_MM) / MILLIMETERS_PER_CENTIMETER),
  );

  if (minCentimeters > maxCentimeters) {
    return null;
  }

  return Math.min(Math.max(targetCentimeters, minCentimeters), maxCentimeters);
};

const getFittingBoardSpanLength = (
  preferredBoardCount: number,
  currentLength: number,
  boardWidth: number,
  gap: number,
  countMode: BoardCountMode,
): number | null => {
  if (preferredBoardCount <= 0 || currentLength <= 0) {
    return null;
  }

  const targetCentimeters = Math.round(currentLength * CENTIMETERS_PER_METER);
  let bestFit: { centimeters: number; distance: number; countDistance: number } | null = null;

  for (let boardCount = 1; boardCount <= MAX_BOARD_FIT_COUNT; boardCount += 1) {
    const centimeters = getFittingCentimetersForBoardCount(boardCount, boardWidth, gap, targetCentimeters, countMode);

    if (centimeters == null) {
      continue;
    }

    const distance = Math.abs(centimeters - targetCentimeters);
    const countDistance = Math.abs(boardCount - preferredBoardCount);

    if (
      !bestFit ||
      distance < bestFit.distance ||
      (distance === bestFit.distance && countDistance < bestFit.countDistance) ||
      (distance === bestFit.distance && countDistance === bestFit.countDistance && centimeters > bestFit.centimeters)
    ) {
      bestFit = { centimeters, distance, countDistance };
    }
  }

  return bestFit ? bestFit.centimeters / CENTIMETERS_PER_METER : null;
};

const getDimensionLabel = (fieldName: DimensionFieldName, labels: Record<DimensionFieldName, string>): string =>
  labels[fieldName];

const BoardFitWarning: FC<BoardFitWarningProps> = ({
  boardFit,
  boardQuantity,
  onApplyBoardFit,
  onApplyDifferentBoardWidthFit,
  appliedFit,
  onAppliedFitChange: setAppliedFit,
  onFitApplied,
}) => {
  const i18n = useI18n();
  const measurementFormat = useMeasurementFormat();
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const { control, setValue } = useFormContext<DeckDesignerConfigurationFormValues>();
  // A viewer still sees the warning and the suggestions, but can't apply (or undo) them.
  const isReadOnly = useReadOnly();

  const shape = useWatch({ control, name: "shape", exact: true });
  const base = useWatch({ control, name: "base", exact: true });
  const height = useWatch({ control, name: "height", exact: true });
  const secondaryBase = useWatch({ control, name: "secondaryBase", exact: true });
  const secondaryHeight = useWatch({ control, name: "secondaryHeight", exact: true });
  const hasHorizontalBoards = useWatch({ control, name: "hasHorizontalBoards", exact: true });
  const boardWidth = useWatch({ control, name: "boardWidth", exact: true });
  const gap = useWatch({ control, name: "gap", exact: true });
  const longDifferentBoardWidth = useWatch({ control, name: "longDifferentBoardWidth", exact: true });
  const shortDifferentBoardWidth = useWatch({ control, name: "shortDifferentBoardWidth", exact: true });
  const longDifferentBoardPosition = useWatch({ control, name: "longDifferentBoardPosition", exact: true });
  const shortDifferentBoardPosition = useWatch({ control, name: "shortDifferentBoardPosition", exact: true });

  const hasIssue = boardFit?.hasIssue ?? false;

  const captureSnapshot = (): BoardFitSnapshot => ({
    base,
    height,
    secondaryBase,
    secondaryHeight,
    boardWidth,
    longDifferentBoardWidth,
    shortDifferentBoardWidth,
    longDifferentBoardPosition,
    shortDifferentBoardPosition,
  });

  const handleUndo = () => {
    if (!appliedFit) {
      return;
    }

    const { snapshot } = appliedFit;

    setValue("base", snapshot.base, { shouldDirty: true, shouldValidate: true });
    setValue("height", snapshot.height, { shouldDirty: true, shouldValidate: true });
    setValue("secondaryBase", snapshot.secondaryBase, { shouldDirty: true, shouldValidate: true });
    setValue("secondaryHeight", snapshot.secondaryHeight, { shouldDirty: true, shouldValidate: true });
    setValue("boardWidth", snapshot.boardWidth, { shouldDirty: true, shouldValidate: true });
    setValue("longDifferentBoardWidth", snapshot.longDifferentBoardWidth, {
      shouldDirty: true,
    });
    setValue("shortDifferentBoardWidth", snapshot.shortDifferentBoardWidth, {
      shouldDirty: true,
    });
    setValue("longDifferentBoardPosition", snapshot.longDifferentBoardPosition, {
      shouldDirty: true,
    });
    setValue("shortDifferentBoardPosition", snapshot.shortDifferentBoardPosition, {
      shouldDirty: true,
    });
    setAppliedFit(null);
  };

  const selectedShape = shape === RECTANGULAR_SHAPE ? RECTANGULAR_SHAPE : "lShape";

  const dimensionLabels = useMemo<Record<DimensionFieldName, string>>(
    () => ({
      base: calculationTranslation.maxBase,
      height: calculationTranslation.maxHeight,
      secondaryBase: calculationTranslation.secondaryBase,
      secondaryHeight: calculationTranslation.secondaryHeight,
    }),
    [
      calculationTranslation.maxBase,
      calculationTranslation.maxHeight,
      calculationTranslation.secondaryBase,
      calculationTranslation.secondaryHeight,
    ],
  );

  const updateDimensionValue = (fieldName: DimensionFieldName, currentValue: number | null, nextValue: number) => {
    if (hasMatchingDimension(currentValue, nextValue)) {
      return;
    }

    setValue(fieldName, nextValue, { shouldDirty: true, shouldValidate: true });
  };

  const boardWidthFormatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.translation.languageCode ?? undefined, {
        maximumFractionDigits: 0,
      }),
    [i18n.translation.languageCode],
  );
  const formatBoardWidth = useCallback(
    (value: number) =>
      measurementFormat.length(value, "mm", `${boardWidthFormatter.format(value)} ${i18n.translation.common.unitMm}`),
    [boardWidthFormatter, i18n.translation.common.unitMm, measurementFormat],
  );
  const suggestedBoardWidthText =
    boardFit?.suggestedBoardWidth == null ? null : formatBoardWidth(boardFit.suggestedBoardWidth);
  const suggestedBoardWidthMessage = suggestedBoardWidthText ? (
    <span className={styles.fitMessageContent}>
      <strong>{calculationTranslation.setBoardWidthTo}</strong>
      <span>{suggestedBoardWidthText}</span>
    </span>
  ) : null;
  const formatOptionalBoardWidth = useCallback(
    (value: number | null) => (value == null ? "—" : formatBoardWidth(value)),
    [formatBoardWidth],
  );
  const longDifferentBoardWidthLabel = calculationTranslation.longDifferentBoardWidth;
  const formatDifferentBoardWidthItems = useCallback(
    (items: DifferentBoardWidthItem[]) =>
      items.map((item) => `${item.label}: ${formatBoardWidth(item.value)}`).join(", "),
    [formatBoardWidth],
  );
  const suggestedDifferentBoardWidthText = useMemo(() => {
    const suggestions = [
      boardFit?.suggestedLongDifferentBoardWidth == null
        ? null
        : {
            label: longDifferentBoardWidthLabel,
            value: boardFit.suggestedLongDifferentBoardWidth,
          },
      selectedShape === RECTANGULAR_SHAPE || boardFit?.suggestedShortDifferentBoardWidth == null
        ? null
        : {
            label: calculationTranslation.shortDifferentBoardWidth,
            value: boardFit.suggestedShortDifferentBoardWidth,
          },
    ].filter((suggestion): suggestion is DifferentBoardWidthItem => suggestion != null);

    if (suggestions.length === 0) {
      return null;
    }

    return formatDifferentBoardWidthItems(suggestions);
  }, [
    boardFit?.suggestedLongDifferentBoardWidth,
    boardFit?.suggestedShortDifferentBoardWidth,
    formatDifferentBoardWidthItems,
    longDifferentBoardWidthLabel,
    calculationTranslation.shortDifferentBoardWidth,
    selectedShape,
  ]);
  const suggestedDifferentBoardWidthMessage = suggestedDifferentBoardWidthText ? (
    <span className={styles.fitMessageContent}>
      <strong>{calculationTranslation.setDifferentBoardWidthTo}</strong>
      <span>{suggestedDifferentBoardWidthText}</span>
    </span>
  ) : null;
  // Only shown (and applied) with a fit issue: no need for its board count loops otherwise.
  const fittingDeckDimensions = useMemo<FittingDeckDimensions | null>(() => {
    if (!hasIssue || !boardQuantity || !isPositiveNumber(boardWidth) || !isNonNegativeNumber(gap)) {
      return null;
    }

    const primaryFieldName = hasHorizontalBoards === false ? "base" : "height";
    const currentPrimaryValue = primaryFieldName === "base" ? base : height;

    if (!isPositiveNumber(currentPrimaryValue)) {
      return null;
    }

    if (selectedShape === RECTANGULAR_SHAPE) {
      const primaryValue = getFittingBoardSpanLength(
        boardQuantity.total,
        currentPrimaryValue,
        boardWidth,
        gap,
        "round",
      );

      return primaryValue == null ? null : { primaryFieldName, primaryValue };
    }

    const secondaryFieldName = hasHorizontalBoards === false ? "secondaryBase" : "secondaryHeight";
    const currentSecondaryValue = secondaryFieldName === "secondaryBase" ? secondaryBase : secondaryHeight;

    if (!isPositiveNumber(currentSecondaryValue) || currentSecondaryValue >= currentPrimaryValue) {
      return null;
    }

    const longLength = getFittingBoardSpanLength(
      boardQuantity.long,
      currentPrimaryValue - currentSecondaryValue,
      boardWidth,
      gap,
      "round",
    );
    const shortLength = getFittingBoardSpanLength(boardQuantity.short, currentSecondaryValue, boardWidth, gap, "floor");

    if (longLength == null || shortLength == null) {
      return null;
    }

    return {
      primaryFieldName,
      primaryValue: longLength + shortLength,
      secondaryFieldName,
      secondaryValue: shortLength,
    };
  }, [
    base,
    boardQuantity,
    boardWidth,
    gap,
    hasHorizontalBoards,
    hasIssue,
    height,
    secondaryBase,
    secondaryHeight,
    selectedShape,
  ]);
  const deckDimensionFormatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.translation.languageCode ?? undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [i18n.translation.languageCode],
  );
  const formatDeckDimension = useCallback(
    (value: number) =>
      measurementFormat.length(value, "m", `${deckDimensionFormatter.format(value)} ${i18n.translation.common.unitM}`),
    [deckDimensionFormatter, i18n.translation.common.unitM, measurementFormat],
  );
  const formatDimensionValue = useCallback(
    (fieldName: DimensionFieldName, value: number) =>
      `${getDimensionLabel(fieldName, dimensionLabels)}: ${formatDeckDimension(value)}`,
    [dimensionLabels, formatDeckDimension],
  );
  const formatMeters = useCallback(
    (value: number | null) => (value == null ? "—" : formatDeckDimension(value)),
    [formatDeckDimension],
  );
  const fittingDeckDimensionsText = useMemo(() => {
    if (!fittingDeckDimensions) {
      return null;
    }

    return [
      formatDimensionValue(fittingDeckDimensions.primaryFieldName, fittingDeckDimensions.primaryValue),
      fittingDeckDimensions.secondaryFieldName && fittingDeckDimensions.secondaryValue != null
        ? formatDimensionValue(fittingDeckDimensions.secondaryFieldName, fittingDeckDimensions.secondaryValue)
        : null,
    ]
      .filter(Boolean)
      .join(", ");
  }, [fittingDeckDimensions, formatDimensionValue]);
  const fittingDeckDimensionsMessage = fittingDeckDimensionsText ? (
    <span className={styles.fitMessageContent}>
      <strong>{calculationTranslation.setDeckDimensionsTo}</strong>
      <span>{fittingDeckDimensionsText}</span>
    </span>
  ) : null;

  const getDimensionValue = (fieldName: DimensionFieldName): number | null => {
    if (fieldName === "base") {
      return base;
    }

    if (fieldName === "height") {
      return height;
    }

    if (fieldName === "secondaryBase") {
      return secondaryBase;
    }

    return secondaryHeight;
  };

  const applyFittingDeckDimensions = () => {
    if (!fittingDeckDimensions) {
      return;
    }

    updateDimensionValue(
      fittingDeckDimensions.primaryFieldName,
      getDimensionValue(fittingDeckDimensions.primaryFieldName),
      fittingDeckDimensions.primaryValue,
    );

    if (fittingDeckDimensions.secondaryFieldName && fittingDeckDimensions.secondaryValue != null) {
      updateDimensionValue(
        fittingDeckDimensions.secondaryFieldName,
        getDimensionValue(fittingDeckDimensions.secondaryFieldName),
        fittingDeckDimensions.secondaryValue,
      );
    }
  };

  const handleApplyBoardWidth = () => {
    setAppliedFit({
      snapshot: captureSnapshot(),
      rows: [
        {
          label: calculationTranslation.boardWidth,
          previous: formatOptionalBoardWidth(boardWidth),
          current: formatOptionalBoardWidth(boardFit?.suggestedBoardWidth ?? null),
        },
      ],
    });
    onApplyBoardFit();
    onFitApplied?.();
  };

  const handleApplyDeckDimensions = () => {
    if (!fittingDeckDimensions) {
      return;
    }

    const { primaryFieldName, primaryValue, secondaryFieldName, secondaryValue } = fittingDeckDimensions;

    setAppliedFit({
      snapshot: captureSnapshot(),
      rows: [
        {
          label: getDimensionLabel(primaryFieldName, dimensionLabels),
          previous: formatMeters(getDimensionValue(primaryFieldName)),
          current: formatMeters(primaryValue),
        },
        ...(secondaryFieldName && secondaryValue != null
          ? [
              {
                label: getDimensionLabel(secondaryFieldName, dimensionLabels),
                previous: formatMeters(getDimensionValue(secondaryFieldName)),
                current: formatMeters(secondaryValue),
              },
            ]
          : []),
      ],
    });
    applyFittingDeckDimensions();
    onFitApplied?.();
  };

  const handleApplyDifferentBoardWidth = () => {
    setAppliedFit({
      snapshot: captureSnapshot(),
      rows: [
        boardFit?.suggestedLongDifferentBoardWidth != null
          ? {
              label: longDifferentBoardWidthLabel,
              previous: formatOptionalBoardWidth(longDifferentBoardWidth),
              current: formatOptionalBoardWidth(boardFit.suggestedLongDifferentBoardWidth),
            }
          : null,
        selectedShape !== RECTANGULAR_SHAPE && boardFit?.suggestedShortDifferentBoardWidth != null
          ? {
              label: calculationTranslation.shortDifferentBoardWidth,
              previous: formatOptionalBoardWidth(shortDifferentBoardWidth),
              current: formatOptionalBoardWidth(boardFit.suggestedShortDifferentBoardWidth),
            }
          : null,
      ].filter((row): row is AppliedFitRow => row != null),
    });
    onApplyDifferentBoardWidthFit();
    onFitApplied?.();
  };

  if (!hasIssue) {
    if (!appliedFit) {
      return null;
    }

    return (
      <MessageBox
        className={`${styles.fitMessage} ${styles.fitWarningBox}`}
        type="informative"
        content={
          <div className={styles.fitAppliedContent}>
            <strong>{calculationTranslation.boardFitApplied}</strong>
            <div className={styles.fitWarningRow}>
              <Table dense>
                <thead>
                  <TableRow>
                    <TableHeadCell align="left">{calculationTranslation.appliedFitParameter}</TableHeadCell>
                    <TableHeadCell align="right">{calculationTranslation.appliedFitPrevious}</TableHeadCell>
                    <TableHeadCell align="right">{calculationTranslation.appliedFitCurrent}</TableHeadCell>
                  </TableRow>
                </thead>
                <tbody>
                  {appliedFit.rows.map((row) => (
                    <TableRow key={row.label}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell className={styles.numericCell}>{row.previous}</TableCell>
                      <TableCell className={styles.numericCell}>
                        <strong>{row.current}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
              <Button variant="primary" className={styles.fitAction} disabled={isReadOnly} onClick={handleUndo}>
                {i18n.translation.common.undo}
              </Button>
            </div>
          </div>
        }
      />
    );
  }

  return (
    <MessageBox
      className={`${styles.fitMessage} ${styles.fitWarningBox}`}
      type="warning"
      content={
        <div className={styles.fitWarningContent}>
          <span>{calculationTranslation.boardFitWarning}</span>
          <div className={styles.fitWarningOptions}>
            {suggestedBoardWidthMessage && (
              <div className={styles.fitWarningRow}>
                <MessageBox className={styles.fitMessage} type="informative" content={suggestedBoardWidthMessage} />
                <Button
                  variant="primary"
                  className={styles.fitAction}
                  disabled={isReadOnly}
                  onClick={handleApplyBoardWidth}
                >
                  {calculationTranslation.setBoardWidth}
                </Button>
              </div>
            )}
            {fittingDeckDimensionsMessage && (
              <div className={styles.fitWarningRow}>
                <MessageBox className={styles.fitMessage} type="informative" content={fittingDeckDimensionsMessage} />
                <Button
                  variant="primary"
                  className={styles.fitAction}
                  disabled={isReadOnly}
                  onClick={handleApplyDeckDimensions}
                >
                  {calculationTranslation.setDeckDimensions}
                </Button>
              </div>
            )}
            {suggestedDifferentBoardWidthMessage && (
              <div className={styles.fitWarningRow}>
                <MessageBox
                  className={styles.fitMessage}
                  type="informative"
                  content={suggestedDifferentBoardWidthMessage}
                />
                <Button
                  variant="primary"
                  className={styles.fitAction}
                  disabled={isReadOnly}
                  onClick={handleApplyDifferentBoardWidth}
                >
                  {calculationTranslation.setDifferentBoardWidth}
                </Button>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
};

// Memoized: DeckDesignerResults re-renders on every result change, but this component's props are
// stable - it re-renders through its own watched fields and props only.
export default memo(BoardFitWarning);
